import type { Workbook } from 'exceljs';
import { excelNumFmt, toDate } from './format';
import { cellValue, columnCaption, computeSummary, lookupText, summaryText, cellTextOf, shownTextOf } from './model';
import type { AppGridColumn, GridSummary } from './types';

/**
 * Excel export for AppGrid. Writes what the grid shows: visible columns in
 * their current order, rows after search / filters / sort, group header rows
 * (outlined, so Excel can collapse them) and the total row. Numbers and dates
 * are written as values with an Excel number format, not as text, so the
 * sheet can still be summed and sorted.
 */

/** A row tree node as the grid hands it over: a group with children, or a data row. */
export type ExportNode<T> =
  | { kind: 'group'; caption: string; children: ExportNode<T>[]; leaves: T[] }
  | { kind: 'data'; row: T };

export interface ExportInput<T> {
  columns: AppGridColumn<T>[];
  nodes: ExportNode<T>[];
  summary?: GridSummary;
  /** All data rows after filtering, for the total row. */
  allRows: T[];
  /** Every column of the grid, hidden ones included, for summaries over a
   *  column that is not exported (a hidden count). Default: `columns`. */
  allColumns?: AppGridColumn<T>[];
  sheetName?: string;
  /** Excel's filter buttons on the header row (DevExtreme autoFilterEnabled). */
  autoFilter?: boolean;
}

export interface ExportedSheet {
  header: string[];
  rows: { kind: 'group' | 'data' | 'total'; level: number; values: unknown[] }[];
}

function exportValue<T>(c: AppGridColumn<T>, row: T): unknown {
  if (c.calculateDisplayValue) return shownTextOf(c, row);
  const v = cellValue(c, row);
  if (c.lookup) return lookupText(c, v) ?? '';
  if (c.dataType === 'date' || c.dataType === 'datetime') return toDate(v) ?? '';
  if (c.customizeText) return cellTextOf(c, v);
  if (typeof v === 'number' || typeof v === 'boolean') return v;
  if (v instanceof Date) return v;
  if (v == null) return '';
  // Render-only columns (cellRender, no dataField): their text, if any.
  return typeof v === 'object' ? '' : String(v);
}

/** The sheet as plain data (what the tests check). */
export function buildExportSheet<T>(input: ExportInput<T>): ExportedSheet {
  const { columns, nodes, summary } = input;
  const out: ExportedSheet = { header: columns.map(columnCaption), rows: [] };
  const groupItems = summary?.groupItems ?? [];
  const byId = new Map((input.allColumns ?? columns).map((c) => [c.name ?? c.dataField ?? '', c]));

  const walk = (list: ExportNode<T>[], level: number) => {
    for (const n of list) {
      if (n.kind === 'data') {
        out.rows.push({ kind: 'data', level, values: columns.map((c) => exportValue(c, n.row)) });
        continue;
      }
      const sums = groupItems
        .map((it) => summaryText(it, computeSummary(it.summaryType, n.leaves, byId.get(it.column)), byId.get(it.column)))
        .filter(Boolean);
      // As on screen (and in DevExtreme's export): the item count only when the
      // grid has no group summaries; with them, the summaries alone.
      const count = groupItems.length ? '' : `${n.leaves.length} ${n.leaves.length === 1 ? 'item' : 'items'}`;
      const suffix = [count, ...sums].filter(Boolean).join(', ');
      const text = suffix ? `${n.caption} (${suffix})` : n.caption;
      out.rows.push({ kind: 'group', level, values: [text, ...columns.slice(1).map(() => '')] });
      walk(n.children, level + 1);
    }
  };
  walk(nodes, 0);

  const totals = summary?.totalItems ?? [];
  if (totals.length) {
    const values: unknown[] = columns.map(() => '');
    for (const it of totals) {
      const at = columns.findIndex((c) => (c.name ?? c.dataField) === (it.showInColumn ?? it.column));
      if (at < 0) continue;
      const c = byId.get(it.column);
      const text = summaryText(it, computeSummary(it.summaryType, input.allRows, c), c).replace(/\n/g, ' ');
      values[at] = values[at] ? `${values[at]}; ${text}` : text;
    }
    out.rows.push({ kind: 'total', level: 0, values });
  }
  return out;
}

/**
 * A date as Excel should hold it: the wall-clock date and time the grid shows.
 * ExcelJS stores a Date by its UTC instant, so a local 10/5 00:00 in New York
 * would land in the sheet as 10/5 04:00 (and a 9 PM time on the next day).
 * DevExtreme's exporter wrote the local fields as UTC; this does the same.
 */
export function excelDateValue(v: unknown): unknown {
  if (!(v instanceof Date)) return v;
  return new Date(Date.UTC(v.getFullYear(), v.getMonth(), v.getDate(), v.getHours(), v.getMinutes(), v.getSeconds(), v.getMilliseconds()));
}

/** Writes the sheet into `wb`. Kept separate from file saving so it can be tested. */
export function writeWorkbook<T>(wb: Workbook, input: ExportInput<T>): ExportedSheet {
  const sheet = buildExportSheet(input);
  const ws = wb.addWorksheet(input.sheetName ?? 'Sheet1', { views: [{ state: 'frozen', ySplit: 1 }] });
  ws.columns = input.columns.map((c, i) => ({
    header: sheet.header[i],
    key: String(i),
    width: Math.max(8, Math.round((c.width ?? c.minWidth ?? 100) / 7)),
    style: { numFmt: excelNumFmt(c.format, c.dataType) },
  }));
  ws.getRow(1).font = { bold: true };
  if (input.autoFilter && input.columns.length) ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: input.columns.length } };
  for (const r of sheet.rows) {
    const row = ws.addRow(r.values.map(excelDateValue));
    if (r.kind === 'group') {
      row.font = { bold: true };
      row.outlineLevel = r.level;
    } else if (r.kind === 'data') {
      row.outlineLevel = r.level;
    } else {
      row.font = { bold: true };
    }
  }
  return sheet;
}

export async function exportGridToExcel<T>(input: ExportInput<T>, fileName: string): Promise<void> {
  const [{ Workbook: WorkbookCtor }, { saveAs }] = await Promise.all([import('exceljs'), import('file-saver')]);
  const wb = new WorkbookCtor();
  writeWorkbook(wb, input);
  const buffer = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`);
}
